import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { toast } from 'react-toastify';

import useBeforeUnloadGuard from '../../hooks/useBeforeUnloadGuard';
import useSessionStore from '../../hooks/useSessionStore';
import { LongSessionFailureCause } from '../../utils/longSession';
import FirebaseContext from '../FirebaseContext';

import LongSessionUploadContext, {
  LongSessionUploadActionsContext,
  initialValues,
} from './LongSessionUploadContext';
import texts from './texts.json';

/** What an upload is doing right now. The owning document carries the server-side stages. */
const UploadState = {
  UPLOADING: 'UPLOADING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

/** What an upload is for. The global list on the workouts table shows workout uploads only. */
const UploadKind = {
  WORKOUT: 'WORKOUT',
  WELCOME_VIDEO: 'WELCOME_VIDEO',
};

const TOAST_OPTIONS = { position: 'top-right', autoClose: 5000 };

/**
 * Owns the Firebase Storage upload tasks for the videos the long-session pipeline processes: a
 * long-format session's, and a product's welcome video.
 *
 * Mounted **above the router** so a task is unaffected by route changes: a coach can start a
 * 40-minute upload and keep working anywhere in the dashboard. Everything that renders an upload —
 * the builder panel, the global indicator — is a *view* of this provider rather than an owner.
 *
 * Uploads are held in a map keyed by the caller's key (a workout id, or `product-<id>`) rather than
 * as a single task. A coach can genuinely start a second session while the first is still
 * transferring, and a single slot would leave the first one running invisibly, with no way to cancel
 * it and no progress shown anywhere.
 *
 * What is deliberately not here:
 *  - Percentage is never written to Firestore. It is local to this browser session, so persisting it
 *    would be write amplification on a document that has listeners, for a number whose bytes die
 *    with the tab.
 *  - Nothing tells the server an upload finished. `onObjectFinalized` fires from Cloud Storage
 *    itself, so a completion call would duplicate a trigger we already get and add a failure mode
 *    (upload succeeds, notification does not) that currently cannot exist.
 *
 * A reload loses the bytes but never the document: it stays PENDING/UPLOADING and the panel offers
 * a re-send, which is simply "no entry in this session".
 *
 * The provider never writes a document itself. Each upload brings an `onStopped` that records, on
 * whatever owns it, why the video never arrived.
 */
const LongSessionUploadContextProvider = ({ children }) => {
  const { firebase: { storage } } = useContext(FirebaseContext);
  const { userId } = useSessionStore();
  const [uploads, setUploads] = useState(initialValues.uploads);
  const uploadTasksRef = useRef({});
  const stopHandlersRef = useRef({});

  /** Merges changes into one upload's entry. A no-op if the entry is gone, or if nothing changed. */
  const updateUpload = useCallback((key, changes) => {
    setUploads((current) => {
      const upload = current[key];
      if (!upload) {
        return current;
      }
      const updated = { ...upload, ...changes };
      /*
        The SDK reports progress on every byte-count change — thousands of events across a
        multi-gigabyte upload — but the displayed percentage only ever takes 101 values. Returning
        the same state object makes React skip the update, which matters because this provider sits
        above the router: every accepted update walks the whole dashboard's fiber tree looking for
        consumers, during the exact window the coach is supposed to be able to keep working.
      */
      const isUnchanged = Object.keys(changes).every((field) => upload[field] === updated[field]);
      return isUnchanged ? current : { ...current, [key]: updated };
    });
  }, []);

  /** Records why the video never arrived, through the upload's own `onStopped`. Never rethrows. */
  const recordUploadStopped = useCallback((key, cause, message, cancelledBy) => {
    const onStopped = stopHandlersRef.current[key];
    if (!onStopped) {
      return;
    }
    Promise.resolve()
      .then(() => onStopped({ cause, message, cancelledBy }))
      .catch((error) => {
        Sentry.captureException(error, {
          extra: {
            message: 'Failed to record a stopped session video upload',
            key,
            cause,
          },
        });
      });
  }, []);

  /** Forgets an upload entirely. Only cancelling does this — a finished one stays, as COMPLETED. */
  const removeUpload = useCallback((key) => {
    delete uploadTasksRef.current[key];
    delete stopHandlersRef.current[key];
    setUploads((current) => {
      const { [key]: removed, ...rest } = current;
      return rest;
    });
  }, []);

  /**
   * Starts uploading a video. Its document must already be waiting in PENDING at UPLOADING — the
   * upload path is keyed on its id, and the document is what makes the upload observable after a
   * reload.
   *
   * @param {Object} params
   * @param {string} params.key Names the upload: the workout id, or `product-<id>` for a welcome video.
   * @param {string} params.kind One of `UploadKind`.
   * @param {string} params.coachId Coach who owns the document; a CAM uploads to the coach's prefix.
   * @param {string} params.storagePath Where to upload, as recorded on the document.
   * @param {File} params.file
   * @param {Function} params.onStopped Records `{ cause, message, cancelledBy }` on the document when
   * the video never arrives (the SDK gave up, or the upload was cancelled).
   * @returns {Promise<boolean>} Resolves true when every byte landed.
   */
  const startUpload = useCallback(({
    key,
    kind,
    coachId,
    storagePath,
    file,
    onStopped,
  }) => {
    const uploadTask = storage.ref().child(storagePath).put(file, { contentType: file.type });

    // Replacing a document's own video supersedes its previous attempt rather than racing it.
    uploadTasksRef.current[key]?.cancel();
    uploadTasksRef.current[key] = uploadTask;
    stopHandlersRef.current[key] = onStopped;

    setUploads((current) => ({
      ...current,
      [key]: {
        key,
        kind,
        coachId,
        fileName: file.name,
        progress: 0,
        state: UploadState.UPLOADING,
      },
    }));

    return new Promise((resolve) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = snapshot.totalBytes
            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            : 0;
          updateUpload(key, { progress });
        },
        (error) => {
          // A cancel arrives here too; it is not a failure and the caller has already cleared state.
          if (error.code === 'storage/canceled') {
            resolve(false);
            return;
          }
          delete uploadTasksRef.current[key];
          Sentry.captureException(error, {
            extra: {
              message: 'Failed to upload a session video',
              storagePath,
            },
          });
          updateUpload(key, { state: UploadState.FAILED });
          recordUploadStopped(key, LongSessionFailureCause.UPLOAD_ERROR, error.message);
          resolve(false);
        },
        () => {
          /*
            The task is finished, so it leaves the ref exactly as the error path does. A cancel that
            arrives later — Discard issues one — must not read a completed transfer as still in
            flight and record a CANCELLED over the stage the server wrote.
          */
          delete uploadTasksRef.current[key];
          /*
            The entry is kept rather than removed. Until onObjectFinalized writes UPLOADED the
            document still reads UPLOADING, so dropping it here is indistinguishable from a dead
            task and the builder would offer a re-send for a video that already landed.
          */
          updateUpload(key, { state: UploadState.COMPLETED, progress: 100 });
          toast.success(texts.uploadComplete, TOAST_OPTIONS);
          resolve(true);
        },
      );
    });
  }, [
    storage,
    updateUpload,
    recordUploadStopped,
  ]);

  /**
   * Abandons one transfer. The document survives — discarding it is a separate action — but it is
   * marked, so the coach is not left with an upload that looks like it is still working.
   */
  const cancelUpload = useCallback((key) => {
    const wasTransferring = !!uploadTasksRef.current[key];
    uploadTasksRef.current[key]?.cancel();
    if (wasTransferring) {
      recordUploadStopped(key, LongSessionFailureCause.CANCELLED, '', userId);
    }
    removeUpload(key);
  }, [removeUpload, recordUploadStopped, userId]);

  const hasTransferInFlight = Object.values(uploads)
    .some(({ state }) => state === UploadState.UPLOADING);

  useBeforeUnloadGuard(hasTransferInFlight);

  const context = useMemo(() => ({ uploads }), [uploads]);

  const actions = useMemo(() => ({
    startUpload,
    cancelUpload,
  }), [
    startUpload,
    cancelUpload,
  ]);

  return (
    <LongSessionUploadActionsContext.Provider value={actions}>
      <LongSessionUploadContext.Provider value={context}>
        {children}
      </LongSessionUploadContext.Provider>
    </LongSessionUploadActionsContext.Provider>
  );
};

LongSessionUploadContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LongSessionUploadContextProvider;
export { UploadState, UploadKind };
